import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, DoCheck, Input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { TableWDropdownComponent2 } from '../table-w-dropdown-2/table-w-dropdown-2.component';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

type RowData = Record<string, any>;

interface PopupHeaderColumnConfig {
  key: string;
  label?: string;
}

interface PopupSummaryField {
  key: string;
  label: string;
  fallbackDetailKey?: string;
}

interface PopupFooterField {
  key: string;
  label: string;
  suffix?: string;
}

interface ButtonApiConfig {
  apiUrl?: string;
  method?: string;
  rowDataKey?: string;
  payload?: Record<string, any>;
  rowFieldMap?: Record<string, string>;
  successMessage?: string;
  errorMessage?: string;
}

interface CellClickApiConfig {
  enabled?: boolean;
  apiUrl?: string;
  method?: string;
  payload?: Record<string, any>;
  payloadKey?: string;
  queryParams?: Record<string, any>;
  responsePath?: string;
  responseKey?: string;
  popupTitle?: string;
  popupHeaderColumns?: PopupHeaderColumnConfig[];
  popupColumns?: string[];
  popupColumnLabels?: Record<string, string>;
  clickableValues?: any[];
  includeCurrentFilters?: boolean;
  includeRowData?: boolean;
  includeTablePayload?: boolean;
  rowPayloadMap?: Record<string, string>;
  useArrayForClickedValue?: boolean;
  emptyMessage?: string;
  errorMessage?: string;
  loadingMessage?: string;
  dummyData?: RowData[];
  detailWidgetId?: string;
  detailFilterKey?: string;
  popupSummaryConfig?: {
    responseKey?: string;
    fields?: PopupSummaryField[];
  };
  popupFooterConfig?: {
    responseKey?: string;
    fields?: PopupFooterField[];
  };
  popupDetailResponseKey?: string;
  popupColumnMap?: Record<string, any>;
  popupApproveButton?: { enabled?: boolean; label?: string };
  popupApproveApi?: {
    apiUrl?: string;
    method?: string;
    rowDataKey?: string;
    payload?: Record<string, any>;
    successMessage?: string;
    errorMessage?: string;
  };
}

@Component({
  selector: 'app-table-dynamic',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule],
  templateUrl: './table-dynamic.component.html',
  styleUrl: './table-dynamic.component.scss'
})
export class TableDynamicComponent extends TableWDropdownComponent2 implements DoCheck, OnDestroy {
  private readonly initOnlyFilterKey = '__table_dynamic_init__';
  private popupApiSubscription?: Subscription;

  isPopupOpen = false;
  popupLoading = false;
  popupError = '';
  popupTitle = 'Details';
  popupColumns: string[] = [];
  popupColumnLabels: Record<string, string> = {};
  popupRows: RowData[] = [];
  popupEmptyMessage = 'No records found.';
  popupLoadingMessage = 'Loading details...';
  popupSummaryData: Record<string, any> = {};
  popupSummaryFields: PopupSummaryField[] = [];
  popupFooterData: Record<string, any> = {};
  popupFooterFields: PopupFooterField[] = [];
  showPopupApproveButton = false;
  popupApproveLabel = 'Approve';
  popupApproving = false;
  loadingCellButtons = new Map<string, boolean>();
  completedCellButtons = new Map<string, boolean>();
  private popupOriginalDetailRows: RowData[] = [];
  private currentPopupCellConfig: CellClickApiConfig | null = null;

  @Input() override set data(value: any) {
    super.data = this.normalizeInputData(value);
  }

  override get data(): any {
    return super.data;
  }

  constructor(
    private readonly tableGlobalFilterService: GlobalFilterService,
    private readonly tableHttpClient: HttpClient
  ) {
    super(tableGlobalFilterService, tableHttpClient);
  }

  ngDoCheck(): void {
    this.ensureColumnsFromRows();
  }

  override getBodyCellClasses(columnKey: string, row: RowData): Record<string, boolean> {
    const base = super.getBodyCellClasses(columnKey, row);
    const columnConfig = (this.columnConfigMap?.[columnKey] || {}) as Record<string, any>;
    const colorMap = columnConfig['valueColorMap'] as Record<string, string> | undefined;
    if (colorMap && row) {
      const cellValue = String(row[columnKey] ?? '').trim().toLowerCase();
      Object.entries(colorMap).forEach(([matchValue, cssClass]) => {
        base[cssClass] = cellValue === matchValue.toLowerCase();
      });
    }
    return base;
  }

  isButtonColumn(columnKey: string, _row?: RowData): boolean {
    const columnConfig = (this.columnConfigMap?.[columnKey] || {}) as Record<string, any>;
    const type = String(columnConfig['type'] || columnConfig['cellType'] || '').trim().toLowerCase();
    return type === 'button' || columnConfig['isButton'] === true;
  }

  getCellButtonKey(row: RowData, columnKey: string, rowIndex?: number): string {
    if (typeof rowIndex === 'number' && Number.isFinite(rowIndex)) {
      return `${rowIndex}_${columnKey}`;
    }

    const rowIdParts = [
      row?.['id'],
      row?.['line_id'],
      row?.['item_id'],
      row?.['quote_id']
    ]
      .map((value) => String(value ?? '').trim())
      .filter((value) => value.length > 0);
    const rowId = rowIdParts.length > 0 ? rowIdParts.join('|') : JSON.stringify(row);
    return `${rowId}_${columnKey}`;
  }

  isCellButtonLoading(row: RowData, columnKey: string, rowIndex?: number): boolean {
    return this.loadingCellButtons.get(this.getCellButtonKey(row, columnKey, rowIndex)) === true;
  }

  isCellButtonRejected(row: RowData): boolean {
    return this.hasRejectedStatus(row);
  }

  isCellButtonPending(row: RowData, columnKey: string, rowIndex?: number): boolean {
    return this.hasPendingStatus(row) && !this.isCellButtonCompleted(row, columnKey, rowIndex);
  }

  isCellButtonCompleted(row: RowData, columnKey: string, rowIndex?: number): boolean {
    if (this.hasApprovedStatus(row)) {
      return true;
    }
    return this.completedCellButtons.get(this.getCellButtonKey(row, columnKey, rowIndex)) === true;
  }

  isCellButtonDisabled(row: RowData, columnKey: string, rowIndex?: number): boolean {
    return this.isCellButtonLoading(row, columnKey, rowIndex)
      || this.isCellButtonCompleted(row, columnKey, rowIndex)
      || this.isCellButtonRejected(row);
  }

  getButtonLabel(columnKey: string, row: RowData, rowIndex?: number): string {
    const buttonKey = this.getCellButtonKey(row, columnKey, rowIndex);
    const columnConfig = (this.columnConfigMap?.[columnKey] || {}) as Record<string, any>;

    if (this.loadingCellButtons.get(buttonKey)) {
      return 'Processing...';
    }

    if (this.isCellButtonCompleted(row, columnKey, rowIndex)) {
      const completedLabel = String(columnConfig['completedButtonLabel'] || '').trim();
      return completedLabel || 'Approved';
    }

    const configuredLabel = String(columnConfig['buttonLabel'] || '').trim();
    if (configuredLabel) {
      return configuredLabel;
    }

    const displayValue = this.getCellDisplayValue(columnKey, row?.[columnKey], row);
    const normalized = String(displayValue ?? '').trim();
    return normalized || 'Approve';
  }

  onActionButtonClick(row: RowData, columnKey: string, rowIndex?: number, event?: MouseEvent): void {
    event?.stopPropagation();

    if (this.isCellButtonDisabled(row, columnKey, rowIndex)) {
      return;
    }

    const columnConfig = (this.columnConfigMap?.[columnKey] || {}) as Record<string, any>;
    const buttonApi = columnConfig['buttonApi'] as ButtonApiConfig | undefined;
    if (buttonApi) {
      this.executeButtonApi(buttonApi, row, columnKey, rowIndex);
      return;
    }

    if (this.isCellApiClickable(columnKey, row)) {
      this.onCellValueClick(row, columnKey, event);
    }
  }

  isCellApiClickable(columnKey: string, row: RowData): boolean {
    const config = this.resolveCellClickApiConfig(columnKey);
    if (!config || !row) {
      return false;
    }

    const value = row[columnKey];
    if (!this.hasDisplayableValue(value)) {
      return false;
    }

    const allowedValues = this.toStringValues(config.clickableValues);
    if (allowedValues.length === 0) {
      return true;
    }

    return allowedValues.includes(String(value).trim());
  }

  onCellValueClick(row: RowData, columnKey: string, event?: MouseEvent): void {
    event?.stopPropagation();

    const config = this.resolveCellClickApiConfig(columnKey);
    if (!config || !this.isCellApiClickable(columnKey, row)) {
      return;
    }

    const value = row[columnKey];
    const isEnhancedPopup = !!(config.popupSummaryConfig || config.popupDetailResponseKey);
    if (isEnhancedPopup) {
      this.currentPopupCellConfig = config;
    }
    this.openPopup(config, columnKey, value);

    // Check for stored detail rows from GlobalFilterService (skip for enhanced popup)
    if (!isEnhancedPopup) {
      const storedDetailRows = this.getStoredDetailRows(config, row, columnKey);
      if (storedDetailRows.length > 0) {
        this.popupLoading = false;
        this.applyPopupRows(storedDetailRows, config);
        return;
      }
    }

    const inlineRows = this.extractDummyRows(config);
    const request$ = this.buildPopupRequest(config, row, columnKey, value);
    if (!request$) {
      this.popupLoading = false;
      if (inlineRows.length > 0) {
        this.applyPopupRows(inlineRows, config);
      } else {
        this.popupError = String(config.errorMessage || this.data?.popupErrorMessage || 'Popup API is not configured.');
      }
      return;
    }

    this.popupApiSubscription?.unsubscribe();
    this.popupApiSubscription = request$.subscribe({
      next: (response: any) => {
        if (isEnhancedPopup) {
          this.handleEnhancedPopupResponse(response, config);
        } else {
          const rows = this.extractRowsFromPopupResponse(response, config);
          this.applyPopupRows(rows, config);
        }
        this.popupLoading = false;
      },
      error: (error: any) => {
        this.popupRows = [];
        this.popupColumns = [];
        this.popupColumnLabels = {};
        this.popupLoading = false;
        this.popupError = String(config.errorMessage || this.data?.popupErrorMessage || 'Failed to load popup details.');
        console.error('[table-dynamic] Failed to load popup API data', {
          columnKey,
          value,
          config,
          error
        });
      }
    });
  }

  getPopupCellDisplayValue(row: RowData, columnKey: string): string {
    if (!row || !columnKey) {
      return '';
    }

    const value = row[columnKey];
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return String(value);
  }

  private getStoredDetailRows(config: CellClickApiConfig, row: RowData, columnKey: string): RowData[] {
    const detailWidgetId = String(
      (config as any).detailWidgetId
      || (this.data?.widgetId ? `${this.data.widgetId}_details` : '')
    ).trim();
    if (!detailWidgetId) {
      return [];
    }

    const allDetailRows = this.tableGlobalFilterService.getWidgetData(detailWidgetId);
    if (!allDetailRows.length) {
      return [];
    }

    const filterKey = String((config as any).detailFilterKey || columnKey).trim();
    const clickedValue = String(row?.[columnKey] ?? '').trim();
    if (!clickedValue) {
      return allDetailRows;
    }

    const filtered = allDetailRows.filter((detailRow) =>
      String(detailRow?.[filterKey] ?? '').trim() === clickedValue
    );
    return filtered.length > 0 ? filtered : allDetailRows;
  }

  closePopup(): void {
    this.isPopupOpen = false;
    this.popupLoading = false;
    this.popupError = '';
    this.popupRows = [];
    this.popupColumns = [];
    this.popupColumnLabels = {};
    this.popupSummaryData = {};
    this.popupSummaryFields = [];
    this.popupFooterData = {};
    this.popupFooterFields = [];
    this.showPopupApproveButton = false;
    this.popupApproveLabel = 'Approve';
    this.popupApproving = false;
    this.popupOriginalDetailRows = [];
    this.currentPopupCellConfig = null;
    this.popupApiSubscription?.unsubscribe();
    this.popupApiSubscription = undefined;
  }

  override ngOnDestroy(): void {
    this.popupApiSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private normalizeInputData(value: any): any {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    const normalized: any = { ...value };
    const hasConfiguredRows = this.hasConfiguredRows(normalized);
    const hasApiUrl = String(normalized.apiUrl || '').trim().length > 0;

    if (
      (!normalized.payload || typeof normalized.payload !== 'object' || Array.isArray(normalized.payload))
      && normalized.defaultFilters
      && typeof normalized.defaultFilters === 'object'
      && !Array.isArray(normalized.defaultFilters)
    ) {
      normalized.payload = { ...(normalized.defaultFilters as Record<string, any>) };
    }

    if ((!Array.isArray(normalized.headerColumns) || normalized.headerColumns.length === 0) && hasConfiguredRows) {
      const firstRow = this.getFirstRow(normalized);
      if (firstRow) {
        normalized.headerColumns = this.buildHeaderColumnsFromRow(firstRow);
      }
    }

    if (hasApiUrl) {
      if (normalized.callApiOnInit === undefined) {
        normalized.callApiOnInit = !hasConfiguredRows;
      }

      const existingFilterKey = String(normalized.filterKey || normalized.discountTypeFilterKey || '').trim();
      if (!existingFilterKey) {
        normalized.filterKey = this.initOnlyFilterKey;
      }

      if (normalized.callApiOnInit === true && normalized.callApiOnEmptySelection === undefined) {
        normalized.callApiOnEmptySelection = true;
      }

      if (normalized.includeEmptySelectionPayloadKey === undefined && normalized.filterKey === this.initOnlyFilterKey) {
        normalized.includeEmptySelectionPayloadKey = false;
      }
    }

    return normalized;
  }

  private hasConfiguredRows(config: any): boolean {
    const tableRows = Array.isArray(config.tableData) ? config.tableData : [];
    const rows = Array.isArray(config.rows) ? config.rows : [];
    return tableRows.length > 0 || rows.length > 0;
  }

  private getFirstRow(config: any): RowData | null {
    const source = Array.isArray(config.tableData)
      ? config.tableData
      : (Array.isArray(config.rows) ? config.rows : []);

    const firstRow = source.find((item: any) => item && typeof item === 'object' && !Array.isArray(item));
    return firstRow ? ({ ...firstRow } as RowData) : null;
  }

  private buildHeaderColumnsFromRow(row: RowData): Array<{ key: string; label: string }> {
    return Object.keys(row)
      .filter((key) => !String(key).endsWith('Options'))
      .map((key) => ({
        key,
        label: this.formatHeaderLabel(key)
      }));
  }

  private ensureColumnsFromRows(): void {
    if (this.displayedColumns.length > 0 || !Array.isArray(this.dataSource) || this.dataSource.length === 0) {
      return;
    }

    const firstRow = this.dataSource.find((item) => item && typeof item === 'object' && !Array.isArray(item));
    if (!firstRow) {
      return;
    }

    const columns = Object.keys(firstRow).filter((key) => !String(key).endsWith('Options'));
    if (columns.length === 0) {
      return;
    }

    this.displayedColumns = [...columns];
    this.columnLabels = Object.fromEntries(
      columns.map((columnKey) => [columnKey, this.formatHeaderLabel(columnKey)])
    );
    this.columnConfigMap = Object.fromEntries(
      columns.map((columnKey) => [columnKey, { key: columnKey, label: this.columnLabels[columnKey] } as any])
    );
  }

  private resolveCellClickApiConfig(columnKey: string): CellClickApiConfig | null {
    const columnConfig = (this.columnConfigMap?.[columnKey] || {}) as Record<string, any>;
    const columnSpecific = this.normalizeCellClickApiConfig(
      columnConfig['cellClickApi'] || columnConfig['clickApi'] || columnConfig['popupApi']
    );
    const mappedSpecific = this.normalizeCellClickApiConfig(this.data?.cellClickApiByColumn?.[columnKey]);
    const defaults = this.normalizeCellClickApiConfig(this.data?.cellClickApiDefaults);
    const globalConfig = this.normalizeCellClickApiConfig(this.data?.cellClickApi);

    const clickableColumns = new Set(this.toStringValues(this.data?.clickableColumns));
    const enableAllColumns = this.parseBoolean(this.data?.enableClickOnAllColumns) === true;
    const hasColumnSpecificConfig = !!columnSpecific || !!mappedSpecific;
    const isColumnEnabledFromList = clickableColumns.has(columnKey);

    if (!hasColumnSpecificConfig && !enableAllColumns && !isColumnEnabledFromList) {
      return null;
    }

    const merged: CellClickApiConfig = {
      ...(defaults || {}),
      ...(globalConfig || {}),
      ...(mappedSpecific || {}),
      ...(columnSpecific || {})
    };

    const explicitEnabled = this.parseBoolean(merged.enabled);
    if (explicitEnabled === false) {
      return null;
    }

    const apiUrl = String(merged.apiUrl || this.data?.popupApiUrl || '').trim();
    const dummyRows = this.extractDummyRows(merged);
    if (!apiUrl && dummyRows.length === 0) {
      return null;
    }

    return {
      ...merged,
      apiUrl: apiUrl || undefined
    };
  }

  private normalizeCellClickApiConfig(source: any): CellClickApiConfig | null {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return null;
    }

    return { ...(source as CellClickApiConfig) };
  }

  private buildPopupRequest(
    config: CellClickApiConfig,
    row: RowData,
    columnKey: string,
    clickedValue: any
  ): Observable<any> | null {
    const apiUrl = String(config.apiUrl || '').trim();
    if (!apiUrl) {
      return null;
    }

    const method = String(config.method || this.data?.popupApiMethod || this.data?.method || 'POST')
      .trim()
      .toUpperCase();
    const payload = this.buildPopupPayload(config, row, columnKey, clickedValue);

    if (method === 'GET') {
      const queryParams = {
        ...this.toRecord(config.queryParams),
        ...payload
      };
      return this.tableHttpClient.get(apiUrl, { params: this.buildPopupHttpParams(queryParams) });
    }

    return this.tableHttpClient.post(apiUrl, payload);
  }

  private buildPopupPayload(
    config: CellClickApiConfig,
    row: RowData,
    columnKey: string,
    clickedValue: any
  ): Record<string, any> {
    const includeTablePayload = config.includeTablePayload !== false;
    const payload = {
      ...(includeTablePayload ? this.toRecord(this.data?.payload) : {}),
      ...this.toRecord(config.payload)
    };

    const payloadKey = String(config.payloadKey || columnKey).trim();
    if (payloadKey) {
      const payloadValue = config.useArrayForClickedValue === true ? [clickedValue] : clickedValue;
      if (payloadKey.includes('.')) {
        this.setNestedValue(payload, payloadKey, payloadValue);
      } else {
        payload[payloadKey] = payloadValue;
      }
    }

    if (config.includeCurrentFilters === true) {
      const currentFilters = this.tableGlobalFilterService.getCurrentFilters() || {};
      Object.assign(payload, this.toRecord(currentFilters.requestParams), this.toRecord(currentFilters.queryParams));
    }

    const rowPayloadMap = this.toRecord(config.rowPayloadMap);
    Object.entries(rowPayloadMap).forEach(([payloadField, rowField]) => {
      const mappedRowField = String(rowField || '').trim();
      if (!mappedRowField) {
        return;
      }
      payload[payloadField] = row?.[mappedRowField];
    });

    if (config.includeRowData === true) {
      payload['row'] = { ...(row || {}) };
    }

    return payload;
  }

  private buildPopupHttpParams(payload: Record<string, any>): HttpParams {
    let params = new HttpParams();

    Object.entries(payload || {}).forEach(([key, rawValue]) => {
      const values = Array.isArray(rawValue) ? rawValue.flat(Infinity) : [rawValue];
      values
        .map((value) => (value === undefined || value === null ? '' : String(value).trim()))
        .filter((value) => value.length > 0)
        .forEach((value) => {
          params = params.append(key, value);
        });
    });

    return params;
  }

  private extractRowsFromPopupResponse(response: any, config: CellClickApiConfig): RowData[] {
    if (response === undefined || response === null) {
      return [];
    }

    const responsePath = String(config.responsePath || '').trim();
    const responseKey = String(config.responseKey || '').trim();
    let extracted: any = response;

    if (responsePath) {
      extracted = this.getValueAtPath(response, responsePath);
    } else if (responseKey) {
      extracted = response?.[responseKey];
    }

    if (Array.isArray(extracted?.data)) {
      extracted = extracted.data;
    } else if (Array.isArray(extracted?.responseList)) {
      extracted = extracted.responseList;
    }

    if (Array.isArray(extracted)) {
      return extracted
        .filter((item) => item !== undefined && item !== null)
        .map((item) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            return { ...(item as RowData) };
          }
          return { value: item };
        });
    }

    if (Array.isArray(response?.data)) {
      return response.data
        .filter((item: any) => item && typeof item === 'object' && !Array.isArray(item))
        .map((item: any) => ({ ...item }));
    }

    if (Array.isArray(response?.responseList)) {
      return response.responseList
        .filter((item: any) => item && typeof item === 'object' && !Array.isArray(item))
        .map((item: any) => ({ ...item }));
    }

    if (extracted && typeof extracted === 'object' && !Array.isArray(extracted)) {
      return [{ ...(extracted as RowData) }];
    }

    return [];
  }

  private getValueAtPath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0)
      .reduce((acc, segment) => {
        if (acc === undefined || acc === null) {
          return undefined;
        }

        const arrayMatch = segment.match(/^([^[\]]+)\[(\d+)\]$/);
        if (!arrayMatch) {
          return acc?.[segment];
        }

        const value = acc?.[arrayMatch[1]];
        return Array.isArray(value) ? value[Number(arrayMatch[2])] : undefined;
      }, source);
  }

  private applyPopupRows(rows: RowData[], config: CellClickApiConfig): void {
    const safeRows = Array.isArray(rows) ? rows : [];
    this.popupRows = safeRows.map((row) => ({ ...(row || {}) }));
    this.popupError = '';
    this.popupEmptyMessage = String(
      config.emptyMessage || this.data?.popupEmptyMessage || 'No records found.'
    );

    const configuredHeaderColumns = this.normalizePopupHeaderColumns(config.popupHeaderColumns);
    if (configuredHeaderColumns.length > 0) {
      this.popupColumns = configuredHeaderColumns.map((column) => column.key);
      this.popupColumnLabels = Object.fromEntries(
        configuredHeaderColumns.map((column) => [
          column.key,
          String(column.label || this.formatHeaderLabel(column.key)).trim()
        ])
      );
      return;
    }

    const configuredColumns = this.toStringValues(config.popupColumns);
    const configuredLabels = this.toRecord(config.popupColumnLabels);
    if (configuredColumns.length > 0) {
      this.popupColumns = configuredColumns;
      this.popupColumnLabels = Object.fromEntries(
        configuredColumns.map((columnKey) => [
          columnKey,
          String(configuredLabels[columnKey] || this.formatHeaderLabel(columnKey)).trim()
        ])
      );
      return;
    }

    const firstRow = this.popupRows.find((row) => row && typeof row === 'object' && !Array.isArray(row));
    if (!firstRow) {
      this.popupColumns = [];
      this.popupColumnLabels = {};
      return;
    }

    const columns = Object.keys(firstRow);
    this.popupColumns = columns;
    this.popupColumnLabels = Object.fromEntries(
      columns.map((columnKey) => [columnKey, this.formatHeaderLabel(columnKey)])
    );
  }

  private openPopup(config: CellClickApiConfig, columnKey: string, clickedValue: any): void {
    const columnLabel = String(this.columnLabels?.[columnKey] || this.formatHeaderLabel(columnKey)).trim();
    const valueLabel = clickedValue === undefined || clickedValue === null ? '' : String(clickedValue).trim();

    this.isPopupOpen = true;
    this.popupLoading = true;
    this.popupError = '';
    this.popupRows = [];
    this.popupColumns = [];
    this.popupColumnLabels = {};
    this.popupEmptyMessage = String(config.emptyMessage || this.data?.popupEmptyMessage || 'No records found.');
    this.popupLoadingMessage = String(config.loadingMessage || this.data?.popupLoadingMessage || 'Loading details...');
    this.popupTitle = String(config.popupTitle || this.data?.popupTitle || '').trim()
      || (valueLabel ? `${columnLabel}: ${valueLabel}` : columnLabel || 'Details');
  }

  private normalizePopupHeaderColumns(source: any): PopupHeaderColumnConfig[] {
    if (!Array.isArray(source)) {
      return [];
    }

    return source
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const key = String((item as any).key || '').trim();
        if (!key) {
          return null;
        }

        const label = String((item as any).label || '').trim();
        return {
          key,
          ...(label ? { label } : {})
        };
      })
      .filter((item): item is PopupHeaderColumnConfig => !!item);
  }

  private extractDummyRows(config: CellClickApiConfig): RowData[] {
    const rows: unknown[] = Array.isArray(config?.dummyData) ? config.dummyData : [];
    return rows
      .filter((item): item is RowData => !!item && typeof item === 'object' && !Array.isArray(item))
      .map((item) => ({ ...item }));
  }

  private toStringValues(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item ?? '').trim())
        .filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }

  private parseBoolean(value: any): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
        return true;
      }
      if (normalized === 'false' || normalized === '0' || normalized === 'no') {
        return false;
      }
    }

    return null;
  }

  onPopupApproveClick(): void {
    const approveConfig = this.currentPopupCellConfig?.popupApproveApi;
    if (!approveConfig) {
      this.closePopup();
      return;
    }

    const apiUrl = String(approveConfig.apiUrl || '').trim();
    if (!apiUrl || this.popupOriginalDetailRows.length === 0) {
      this.closePopup();
      return;
    }

    this.popupApproving = true;
    this.popupError = '';
    const rowDataKey = String(approveConfig.rowDataKey || 'requestData').trim();
    const envelope = this.toRecord(approveConfig.payload);

    const requests = this.popupOriginalDetailRows.map(row => {
      const payload = {
        ...envelope,
        [rowDataKey]: { ...row }
      };
      return this.tableHttpClient.post(apiUrl, payload);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.popupApproving = false;
        this.closePopup();
      },
      error: (error: any) => {
        this.popupApproving = false;
        this.popupError = String(approveConfig.errorMessage || 'Failed to approve. Please try again.');
        console.error('[table-dynamic] Approve API failed', error);
      }
    });
  }

  private handleEnhancedPopupResponse(response: any, config: CellClickApiConfig): void {
    const responseData = this.extractNestedResponseData(response);
    this.applyPopupSummary(responseData, config);
    const detailRows = this.extractPopupDetailRows(responseData, config);
    this.popupOriginalDetailRows = detailRows.map(row => ({ ...row }));
    const mappedRows = this.mapPopupDetailRows(detailRows, config);
    this.applyPopupRows(mappedRows, config);
    this.applyPopupFooter(responseData, config);
    this.applyPopupApproveButton(config);
  }

  private extractNestedResponseData(response: any): Record<string, any> {
    const responseList = response?.responseList;
    if (Array.isArray(responseList) && responseList.length > 0) {
      const data = responseList[0]?.data;
      if (Array.isArray(data) && data.length > 0) {
        return data[0] || {};
      }
    }
    return response || {};
  }

  private applyPopupSummary(responseData: Record<string, any>, config: CellClickApiConfig): void {
    const summaryConfig = config.popupSummaryConfig;
    if (!summaryConfig?.fields?.length) {
      this.popupSummaryFields = [];
      this.popupSummaryData = {};
      return;
    }

    const summaryKey = String(summaryConfig.responseKey || '').trim();
    let summarySource: Record<string, any> = {};
    if (summaryKey && Array.isArray(responseData[summaryKey]) && responseData[summaryKey].length > 0) {
      summarySource = responseData[summaryKey][0];
    }

    const detailKey = String(config.popupDetailResponseKey || '').trim();
    let detailFirstRow: Record<string, any> = {};
    if (detailKey && Array.isArray(responseData[detailKey]) && responseData[detailKey].length > 0) {
      detailFirstRow = responseData[detailKey][0];
    }

    this.popupSummaryFields = summaryConfig.fields;
    this.popupSummaryData = {};
    summaryConfig.fields.forEach(field => {
      let value = summarySource[field.key];
      if ((value === null || value === undefined || value === 'null') && field.fallbackDetailKey) {
        value = detailFirstRow[field.fallbackDetailKey];
      }
      this.popupSummaryData[field.key] = value ?? '';
    });
  }

  private extractPopupDetailRows(responseData: Record<string, any>, config: CellClickApiConfig): RowData[] {
    const detailKey = String(config.popupDetailResponseKey || '').trim();
    if (!detailKey) {
      return [];
    }
    const detailArray = responseData[detailKey];
    if (!Array.isArray(detailArray)) {
      return [];
    }
    return detailArray
      .filter(item => item && typeof item === 'object' && !Array.isArray(item))
      .map(item => ({ ...item }));
  }

  private mapPopupDetailRows(rows: RowData[], config: CellClickApiConfig): RowData[] {
    const columnMap = config.popupColumnMap;
    if (!columnMap) {
      return rows;
    }

    return rows.map(sourceRow => {
      const mapped: RowData = {};
      Object.entries(columnMap).forEach(([displayKey, mapping]) => {
        mapped[displayKey] = this.resolvePopupColumnValue(sourceRow, displayKey, mapping);
      });
      return mapped;
    });
  }

  private resolvePopupColumnValue(sourceRow: RowData, columnKey: string, mapping: any): any {
    if (typeof mapping === 'string') {
      return sourceRow[mapping] ?? '';
    }

    if (typeof mapping === 'object' && mapping) {
      if (mapping.computeType === 'discountedPrice') {
        const price = parseFloat(sourceRow[mapping.priceKey] || '0');
        const discount = parseFloat(sourceRow[mapping.discountKey] || '0');
        return (price * (1 - discount / 100)).toFixed(2);
      }
      if (mapping.computeType === 'revenue') {
        const price = parseFloat(sourceRow[mapping.priceKey] || '0');
        const qty = parseFloat(sourceRow[mapping.qtyKey] || '0');
        const discount = parseFloat(sourceRow[mapping.discountKey] || '0');
        return Math.round(price * (1 - discount / 100) * qty);
      }

      let value = sourceRow[mapping.sourceKey || columnKey] ?? '';
      if (mapping.suffix && String(value).trim()) {
        value = value + mapping.suffix;
      }
      return value;
    }

    return sourceRow[columnKey] ?? '';
  }

  private applyPopupFooter(responseData: Record<string, any>, config: CellClickApiConfig): void {
    const footerConfig = config.popupFooterConfig;
    if (!footerConfig?.fields?.length) {
      this.popupFooterFields = [];
      this.popupFooterData = {};
      return;
    }

    const summaryKey = String(footerConfig.responseKey || config.popupSummaryConfig?.responseKey || '').trim();
    let source: Record<string, any> = {};
    if (summaryKey && Array.isArray(responseData[summaryKey]) && responseData[summaryKey].length > 0) {
      source = responseData[summaryKey][0];
    }

    this.popupFooterFields = footerConfig.fields;
    this.popupFooterData = {};
    footerConfig.fields.forEach(field => {
      const value = source[field.key] ?? '';
      this.popupFooterData[field.key] = value;
    });
  }

  private applyPopupApproveButton(config: CellClickApiConfig): void {
    const btnConfig = config.popupApproveButton;
    this.showPopupApproveButton = !!btnConfig?.enabled;
    this.popupApproveLabel = String(btnConfig?.label || 'Approve').trim();
  }

  private setNestedValue(obj: Record<string, any>, path: string, value: any): void {
    const parts = path.split('.').map(p => p.trim()).filter(p => p.length > 0);
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    const lastPart = parts[parts.length - 1];
    if (lastPart) {
      current[lastPart] = value;
    }
  }

  private executeButtonApi(config: ButtonApiConfig, row: RowData, columnKey: string, rowIndex?: number): void {
    const apiUrl = String(config.apiUrl || '').trim();
    if (!apiUrl) {
      return;
    }

    const buttonKey = this.getCellButtonKey(row, columnKey, rowIndex);
    this.loadingCellButtons.set(buttonKey, true);

    const rowDataKey = String(config.rowDataKey || 'requestData').trim();
    const envelope = this.toRecord(config.payload);
    const rowFieldMap = this.toRecord(config.rowFieldMap);
    const staticRowData = this.toRecord(envelope[rowDataKey]);

    const rowData: Record<string, any> = {};
    if (Object.keys(rowFieldMap).length > 0) {
      Object.entries(rowFieldMap).forEach(([payloadField, rowField]) => {
        rowData[payloadField] = row?.[String(rowField)] ?? null;
      });
    } else {
      Object.assign(rowData, row);
    }

    const payload = {
      ...envelope,
      [rowDataKey]: {
        ...staticRowData,
        ...rowData
      }
    };

    this.tableHttpClient.post(apiUrl, payload).subscribe({
      next: () => {
        this.loadingCellButtons.delete(buttonKey);
        this.completedCellButtons.set(buttonKey, true);
        row['approval_status'] = 'Approved';
        if (Array.isArray(this.dataSource)) {
          this.dataSource = [...this.dataSource];
        }
      },
      error: (error: any) => {
        this.loadingCellButtons.delete(buttonKey);
        console.error('[table-dynamic] Button API call failed', error);
      }
    });
  }

  private hasDisplayableValue(value: any): boolean {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  private hasApprovedStatus(row: RowData): boolean {
    if (!row || typeof row !== 'object') {
      return false;
    }

    const status = String(row['approval_status'] ?? row['approve'] ?? row['status'] ?? '').trim().toLowerCase();
    return ['true', 'approved', 'approval success', 'success'].includes(status);
  }

  private hasRejectedStatus(row: RowData): boolean {
    if (!row || typeof row !== 'object') {
      return false;
    }

    const status = String(row['approval_status'] ?? row['approve'] ?? row['status'] ?? '').trim().toLowerCase();
    return status === 'rejected';
  }

  private hasPendingStatus(row: RowData): boolean {
    if (!row || typeof row !== 'object') {
      return false;
    }

    const status = String(row['approval_status'] ?? row['approve'] ?? row['status'] ?? '').trim().toLowerCase();
    return ['false', 'pending', 'pending approval'].includes(status);
  }
}
